import { DatePickerFormat } from '../../integrations/lavva/types/chart';
import { RangeFromTo } from '../../types/charts';
import { MeasurementRange } from '../../types/measurement';
import { convertDateToFormat } from '../helpers/date-convert';

export const dataPickerFormat: DatePickerFormat = {
  [MeasurementRange.DayInHours]: {
    format: ['eeee, dd.MM', 'eeee, dd.MM HH:mm'],
    onNext: (date: Date) => date.setDate(date.getDate() + 1),
    onPrev: (date: Date) => date.setDate(date.getDate() - 1),
  },
  [MeasurementRange.WeekInDays]: {
    format: ['dd.MM.yy'],
    onNext: (date: Date) => date.setDate(date.getDate() + 7),
    onPrev: (date: Date) => date.setDate(date.getDate() - 7),
  },
  [MeasurementRange.MonthInDays]: {
    format: ['LLLL, yyyy'],
    onNext: (date: Date) => date.setMonth(date.getMonth() + 1),
    onPrev: (date: Date) => date.setMonth(date.getMonth() - 1),
  },
  [MeasurementRange.YearInMonths]: {
    format: ['yyyy'],
    onNext: (date: Date) => date.setFullYear(date.getFullYear() + 1),
    onPrev: (date: Date) => date.setFullYear(date.getFullYear() - 1),
  },
  [MeasurementRange.TenYearsInYears]: {
    format: ['yyyy'],
    onNext: (date: Date) => date.setFullYear(date.getFullYear() + 1),
    onPrev: (date: Date) => date.setFullYear(date.getFullYear() - 1),
  },
};

export const getDateRangeByPage = (activeMeasurementRange: MeasurementRange, page: number): RangeFromTo => {
  const now = new Date();
  const baseDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let fromOutput = new Date();
  let toOutput = new Date();

  switch (activeMeasurementRange) {
    case MeasurementRange.DayInHours: {
      const targetDate = new Date(baseDate);
      targetDate.setDate(targetDate.getDate() + page);

      const from = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 0, 0, 0, 0);

      const to = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }

    case MeasurementRange.WeekInDays: {
      const targetDate = new Date(baseDate);
      targetDate.setDate(targetDate.getDate() + page * 7);

      const dayOfWeek = targetDate.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      const from = new Date(
        targetDate.getFullYear(),
        targetDate.getMonth(),
        targetDate.getDate() - daysToSubtract,
        0,
        0,
        0,
        0,
      );

      const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6, 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }

    case MeasurementRange.MonthInDays: {
      const year = baseDate.getFullYear();
      const month = baseDate.getMonth() + page;

      const from = new Date(year, month, 1, 0, 0, 0, 0);
      const to = new Date(year, month + 1, 0, 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }

    case MeasurementRange.YearInMonths: {
      const year = baseDate.getFullYear() + page;

      const from = new Date(year, 0, 1, 0, 0, 0, 0);
      const to = new Date(year, 11, 31, 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }

    case MeasurementRange.TenYearsInYears: {
      const year = baseDate.getFullYear() + page * 10;

      const from = new Date(year - 9, 0, 1, 0, 0, 0, 0);
      const to = new Date(year, 11, 31, 23, 59, 59, 999);

      fromOutput = from;
      toOutput = to;
      break;
    }
  }

  return {
    from: fromOutput.toISOString(),
    to: toOutput.toISOString(),
  };
};

export const getRangeLabel = (activeMeasurementRange: MeasurementRange, rangeFromTo: RangeFromTo) => {
  let range = '';
  const dateFormat = dataPickerFormat[activeMeasurementRange].format[0];

  switch (activeMeasurementRange) {
    case MeasurementRange.DayInHours: {
      const from = new Date(rangeFromTo.from);

      if (from) range = `${convertDateToFormat(from, dateFormat)}`;

      break;
    }
    case MeasurementRange.WeekInDays: {
      const from = new Date(rangeFromTo.from);
      const to = new Date(rangeFromTo.to);

      if (from && to) range = `${convertDateToFormat(from, dateFormat)} - ${convertDateToFormat(to, dateFormat)}`;

      break;
    }
    case MeasurementRange.MonthInDays: {
      const from = new Date(rangeFromTo.from);

      if (from) range = convertDateToFormat(from, dateFormat);

      break;
    }
    case MeasurementRange.YearInMonths: {
      const to = new Date(rangeFromTo.to);

      if (to) range = `${to.getFullYear()}`;

      break;
    }
    case MeasurementRange.TenYearsInYears: {
      const from = new Date(rangeFromTo.from);
      const to = new Date(rangeFromTo.to);

      const adjustedFrom = new Date(from);
      adjustedFrom.setDate(adjustedFrom.getDate() + 1);

      if (adjustedFrom && to)
        range = `${convertDateToFormat(adjustedFrom, dateFormat)} - ${convertDateToFormat(to, dateFormat)}`;
      break;
    }
  }

  return range;
};
